/**
 * ATTENTION
 * This is just dirty prototype code for the new partner-werden page. Supposed to be removed after MVP phase.
 * ATTENTION
 */

import { graphql } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';
import get from 'lodash/get';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Circle,
  Flex,
  Grid,
  Heading,
  SimpleGrid,
  Text,
} from '@hausgold/designsystem';
import { ChevronRight } from '@hausgold/designsystem/icons';
import { Container } from 'reactstrap';
import ExitIntent from '../../components/ExitIntent';
import ExitIntentModal from '../../components/ExitIntentModal';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import Footer from '../../components/partner-werden-prototype/Footer';
import Header from '../../components/Header';
import {
  FaceAgentIcon,
  HandshakeIcon,
  HomeIcon,
  StarterIcon,
} from '../../components/partner-werden-prototype/content/BenefitCards/Icons';
import {
  analyticsTrack,
  EVENT_OBJECT,
  EVENT_VERB,
  eventBuilder,
} from '../../utils/tracking';
import Section from '../../components/partner-werden-prototype/Section';
import SectionHeading from '../../components/partner-werden-prototype/SectionHeading';
import Link from '../../components/Link';
import teaserImage from '../../components/partner-werden-prototype/content/Teaser/image.png';

const PartnerWerdenPrototype = ({ data, location }) => {
  const page = get(data, 'contentfulPage');
  const site = get(data, 'site');
  const heroImage = get(page, 'heroImage');
  const exitIntent = get(page, 'exitIntent');

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const removeExitIntent = ExitIntent({
      threshold: 10,
      eventThrottle: 100,
      onExitIntent: () => {
        setShowPopup(true);
      },
    });
    return () => {
      removeExitIntent();
    };
  }, []);

  const breadcrumbs = [
    {
      title: 'Startseite',
      url: `${site.siteMetadata.siteUrl}/`,
    },
    {
      title: page.title,
      url: `${site.siteMetadata.siteUrl}/fuer-makler/${page.slug}/`,
    },
  ];

  const content = useMemo(
    () => [
      {
        title: 'Gezielte Kundenakquise',
        icon: <HomeIcon />,
        text: 'Wir qualifizieren verkaufsbereite Eigentümer und vermitteln nur geprüfte Kontakte.',
      },
      {
        title: 'Passgenaue Vermittlung',
        icon: <HandshakeIcon />,
        text: 'Sie bestimmen Regionen und Objekt-Prioritäten, unsere Plattform sorgt für das passende Matching.',
      },
      {
        title: 'Flexible Abnahmemenge',
        icon: <StarterIcon />,
        text: 'Sie entscheiden selbst, welche Objekte Sie annehmen. Keine Mindestabnahme, kein Risiko.',
      },
      {
        title: 'Zwei faire Preismodelle',
        icon: <FaceAgentIcon />,
        text: 'Entscheiden Sie sich für Festpreis oder Gemeinschaftsgeschäft mit Erfolgsbeteiligung.',
      },
    ],
    {}
  );

  const handleClick = () => {
    analyticsTrack({
      event: eventBuilder(EVENT_OBJECT.button, EVENT_VERB.interacted),
      params: {
        label: 'Kostenlos Zugang sichern',
        to: '/koop-anfrage',
      },
    });
  };

  // Copied from components/Header.jsx.....
  return (
    <Layout
      location={location}
      cookieConsent
      header={
        <>
          <Header template="Makler" headerStyles={{ marginTop: 0 }} />
          <Box backgroundColor="blue.700" pt="82px">
            <Box
              bgRepeat="no-repeat"
              bgPosition="bottom right"
              backgroundImage={{ xl: teaserImage }}
              backgroundSize="contain"
              py={12}
              as={Container}
            >
              <Box width={{ xl: '50%' }} textColor="white">
                <Heading as="h1" fontSize="4xl">
                  Mehr Objekte. Mehr Abschlüsse.
                </Heading>
                <Heading as="h2" size="h1">
                  Mit der HAUSGOLD Objektbörse erhalten Sie geprüfte
                  Eigentümerkontakte aus Ihrer Region.
                </Heading>

                <Button
                  marginTop={8}
                  to="/koop-anfrage"
                  as={Link}
                  onClick={handleClick}
                  leftIcon={<ChevronRight />}
                  fontSize="2xl"
                >
                  Kostenlos Zugang sichern
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      }
      footer={<Footer />}
    >
      <Helmet>
        <meta name="content-entity" content="page" />
        <meta name="content-id" content={page.contentful_id} />
      </Helmet>
      <SEO
        entity={{ ...page, imageSrc: getSrc(heroImage) }}
        title={page.metaTitle || page.headline}
        canonical={page.canonical}
        type="page"
        description={page.metaDescription}
        indexable={page.indexable}
        breadcrumbs={breadcrumbs}
      />

      {/* Section 1 Introduction */}
      <Section my={0} py={12} sx={{ '.container': { maxWidth: '780px' } }}>
        <Container>
          <SectionHeading
            fontSize="1.5rem"
            fontWeight="bold"
            justifySelf="center"
            textAlign="center"
          >
            Sie möchten Ihren Bestand gezielt erweitern und planbar mehr
            Verkaufserträge sichern?
          </SectionHeading>
          <Text mb={0} fontSize="lg" textAlign="center">
            Die HAUSGOLD Objektbörse liefert Ihnen geprüfte Eigentümerkontakte
            mit echtem Verkaufsinteresse – direkt in Ihrer Region. So steigern
            Sie Ihre Abschlussquote und erhöhen Ihren Umsatz – ganz ohne
            zusätzlichen Aufwand.
          </Text>
        </Container>
      </Section>

      {/* Section 2 Benefit Cards */}
      <Section my={0} py={12} backgroundColor="blue.700">
        <Container>
          <SectionHeading
            textColor="white"
            fontSize="1.5rem"
            justifySelf="center"
          >
            So erreichen Sie mehr mit weniger Aufwand
          </SectionHeading>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            {content.map(({ title, icon, text }) => {
              const Icon = React.cloneElement(icon, {
                width: 50,
                height: 50,
                color: 'blue.400',
                display: 'inline-block',
              });

              return (
                <Card textAlign="center">
                  <CardHeader border="none">
                    <Box margin="0 auto">
                      {Icon}
                      <br />
                      <span>{title}</span>
                    </Box>
                  </CardHeader>
                  <CardBody>
                    <p>{text}</p>
                  </CardBody>
                </Card>
              );
            })}
          </SimpleGrid>
          <Box textAlign="center">
            <Button
              marginTop={8}
              to="/koop-anfrage"
              as={Link}
              onClick={handleClick}
              leftIcon={<ChevronRight />}
              fontSize="2xl"
            >
              Kostenlos Zugang sichern
            </Button>
          </Box>
        </Container>
      </Section>

      {/* Section 3 Reason */}
      <Section
        my={0}
        py={12}
        // This is a random number which ensures that number descriptions (those with "shy") will fit in two lines on desktop pages.
        sx={{ '.container': { maxWidth: '680px' } }}
      >
        <Container>
          <SectionHeading
            fontSize="1.5rem"
            fontWeight="bold"
            justifySelf="center"
            textAlign="center"
          >
            Darum setzen Makler auf HAUSGOLD
          </SectionHeading>
          <Text
            marginBottom={{ base: 0, md: 8 }}
            fontSize="lg"
            textAlign="center"
          >
            HAUSGOLD ist einer der führenden Partner für digitale Objektaquise
            in Deutschland – mit 100% regionaler Abdeckung und messbarem Erfolg.
          </Text>
          <Grid
            gridTemplateAreas={{
              base: `
            'numberExperience'
            'headlineExperience'
            'numberActiveBroker'
            'headlineActiveBroker'
            'numberExclusiveData'
            'headlineExclusiveData'
            `,
              md: `
            'numberExperience numberActiveBroker numberExclusiveData'
            'headlineExperience headlineActiveBroker headlineExclusiveData'
          `,
            }}
            gridTemplateColumns={{ base: undefined, md: '1fr 1fr 1fr' }}
            paddingX={10}
            paddingTop={{ base: 4, md: 0 }}
            rowGap={0}
            columnGap={4}
            textAlign="center"
            marginBottom={16}
          >
            <Box
              gridArea="numberExperience"
              fontSize="2rem"
              textColor="blue.400"
              fontWeight="bold"
              paddingTop={{ base: 6, md: 0 }}
            >
              10+
            </Box>
            <Box
              gridArea="headlineExperience"
              fontWeight="bold"
              fontSize="xl"
              paddingBottom={2}
            >
              Jahre Er&shy;fah&shy;rung im
              Im&shy;mo&shy;bi&shy;li&shy;en&shy;markt
            </Box>

            <Box
              gridArea="numberActiveBroker"
              fontSize="2rem"
              textColor="blue.400"
              fontWeight="bold"
              paddingTop={{ base: 6, md: 0 }}
            >
              3.000+
            </Box>
            <Box
              gridArea="headlineActiveBroker"
              fontWeight="bold"
              fontSize="xl"
              paddingBottom={2}
            >
              ak&shy;ti&shy;ve Mak&shy;ler im Netz&shy;werk
            </Box>

            <Box
              gridArea="numberExclusiveData"
              fontSize="2rem"
              textColor="blue.400"
              fontWeight="bold"
              paddingTop={{ base: 6, md: 0 }}
            >
              150.000+
            </Box>
            <Box
              gridArea="headlineExclusiveData"
              fontWeight="bold"
              fontSize="xl"
              paddingBottom={2}
            >
              ge&shy;prüf&shy;te
              Ei&shy;gen&shy;tü&shy;mer&shy;kon&shy;tak&shy;te
            </Box>
          </Grid>

          <SectionHeading
            fontSize="1.5rem"
            fontWeight="bold"
            justifySelf="center"
            textAlign="center"
          >
            So funktioniert die Objektbörse
          </SectionHeading>
          <Flex marginBottom={4}>
            <Circle
              backgroundColor="blue.400"
              width="3rem"
              height="3rem"
              textColor="white"
              fontWeight="bold"
              fontSize="2xl"
              marginRight={6}
            >
              1
            </Circle>
            <Text mb={0} fontSize="lg">
              Eigentümer bekunden Verkaufsinteresse über unsere Online-Kanäle
              und Partnernetzwerke.
            </Text>
          </Flex>
          <Flex marginBottom={4}>
            <Circle
              backgroundColor="blue.400"
              width="3rem"
              height="3rem"
              textColor="white"
              fontWeight="bold"
              fontSize="2xl"
              marginRight={6}
            >
              2
            </Circle>
            <Text marginBottom={0} fontSize="lg">
              Wir prüfen die Anfragen telefonisch und erfassen die wichtigsten
              Immobiliendaten.
            </Text>
          </Flex>
          <Flex marginBottom={4}>
            <Circle
              backgroundColor="blue.400"
              width="3rem"
              height="3rem"
              textColor="white"
              fontWeight="bold"
              fontSize="2xl"
              marginRight={6}
            >
              3
            </Circle>
            <Text mb={0} fontSize="lg">
              Im Maklerportal sehen Sie passende Kontakte, wählen sie aus und
              starten direkt in die Akquise.
            </Text>
          </Flex>
          <Box textAlign="center">
            <Button
              marginTop={8}
              to="/koop-anfrage"
              as={Link}
              onClick={handleClick}
              leftIcon={<ChevronRight />}
              fontSize="2xl"
            >
              Kostenlos Zugang sichern
            </Button>
          </Box>
        </Container>
      </Section>

      {/* Section 4 Feedback */}
      <Section
        my={0}
        py={12}
        sx={{ '.container': { maxWidth: '780px' } }}
        backgroundColor="blue.700"
      >
        <Container>
          <Text
            as="q"
            marginBottom={{ base: 4, md: 10 }}
            textAlign="center"
            display="block"
            quotes={`"„" "“"`}
            fontStyle="italic"
            fontSize={{ base: '2xl', md: '4xl' }}
            textColor="white"
            sx={{
              '&::after, &::before': {
                color: 'blue.400',
              },
            }}
          >
            HAUSGOLD hat mit der Verkaufsquote aktuell die besten Kundenkontakte
            am Markt.
          </Text>
          <Text
            textColor="white"
            fontSize={{ base: 'md', md: 'lg' }}
            textAlign="center"
          >
            ein zufriedener HAUSGOLD-Partner
          </Text>
          <Box fontSize="xl" textAlign="center">
            <Link
              to="/fuer-makler/makler-erfahrungen/"
              _hover={{ color: 'blue.300' }}
            >
              Weitere Maklerstimmen ansehen
            </Link>
          </Box>
        </Container>
      </Section>

      {/* Section 5 CTA */}
      <Section my={0} py={12} sx={{ '.container': { maxWidth: '780px' } }}>
        <Container>
          <SectionHeading
            fontSize="1.5rem"
            fontWeight="bold"
            justifySelf="center"
            textAlign="center"
          >
            Jetzt kostenlos Zugang sichern
          </SectionHeading>
          <Text mb={0} fontSize="lg" textAlign="center">
            Erhalten Sie geprüfte Eigentümerkontakte aus Ihrer Region – einfach,
            planbar und effizient.
          </Text>
          <Box textAlign="center">
            <Button
              marginTop={8}
              to="/koop-anfrage"
              as={Link}
              onClick={handleClick}
              leftIcon={<ChevronRight />}
              fontSize="2xl"
            >
              Kostenlos Zugang sichern
            </Button>
          </Box>
        </Container>
      </Section>

      {exitIntent && (
        <ExitIntentModal
          show={showPopup}
          image={exitIntent.image}
          text={exitIntent.text.childMarkdownRemark.html}
          button={exitIntent.button}
          setShowPopup={setShowPopup}
        />
      )}
    </Layout>
  );
};

export default PartnerWerdenPrototype;

export const pageQuery = graphql`
  query B2BPrototype {
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
    contentfulPage(slug: { eq: "partner-werden" }) {
      useArticleSchema
      contentful_id
      id
      title
      headline
      headlineColor
      subTitle
      indexable
      metaTitle
      metaDescription
      slug
      updatedAt
      createdAt
      childPages {
        id
        slug
        title
      }
      page {
        id
        slug
        title
        childPages {
          id
          slug
          title
        }
      }
      formwizard {
        position
        url
      }
      body {
        childMarkdownRemark {
          html
        }
      }
      exitIntent {
        image {
          gatsbyImageData(layout: FIXED, width: 170)
          title
        }
        text {
          childMarkdownRemark {
            html
          }
        }
        button {
          title
          url
          color
          size
          shadow
        }
      }
      relatedPages {
        ... on ContentfulArticle {
          id
          slug
          title
          metaDescription
          relatedArticlesText
          heroImage {
            gatsbyImageData(
              layout: FIXED
              width: 600
              height: 600
              cropFocus: FACES
              resizingBehavior: FILL
            )
          }
          category {
            slug
          }
          description {
            description
            id
            childMarkdownRemark {
              id
              html
            }
          }
        }
      }
      faqList {
        faqItems {
          id
          answer {
            childMarkdownRemark {
              html
            }
          }
          question
        }
      }
      contentBlocks {
        title
        id
        tocPosition
        entries {
          ... on ContentfulDownloadItem {
            internal {
              type
            }
            id
            title
            file {
              file {
                url
              }
              title
            }
            link
          }
          ... on ContentfulDownloadList {
            internal {
              type
            }
            id
            title
            downloadItems {
              title
              file {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulList {
            internal {
              type
            }
            id
            listItems {
              internal {
                type
              }
              children {
                id
              }
              title
              id
              icon
              image {
                gatsbyImageData(layout: FIXED, width: 170)
                title
              }
              text {
                childMarkdownRemark {
                  html
                }
              }
            }
            title
            template
          }
          ... on ContentfulListItem {
            internal {
              type
            }
            children {
              id
            }
            title
            id
            icon
            image {
              gatsbyImageData(layout: FIXED, width: 170)
              title
            }
            text {
              childMarkdownRemark {
                html
              }
            }
          }
          ... on ContentfulText {
            internal {
              type
            }
            id
            text {
              childMarkdownRemark {
                html
              }
            }
          }
          ... on ContentfulTextWithImage {
            internal {
              type
            }
            id
            title
            template
            imageAlignment
            body {
              childMarkdownRemark {
                html
              }
            }
            image {
              gatsbyImageData(layout: FULL_WIDTH, width: 800)
              title
            }
          }
          ... on ContentfulFaqList {
            internal {
              type
            }
            id
            faqItems {
              id
              answer {
                childMarkdownRemark {
                  html
                }
              }
              question
            }
          }
          ... on ContentfulButton {
            internal {
              type
            }
            id
            color
            shadow
            size
            title
            url
          }
          ... on ContentfulFormwizard {
            internal {
              type
            }
            id
            position
            title
            url
            variant
          }
        }
      }
      heroImage {
        gatsbyImageData(
          layout: FIXED
          width: 1920
          height: 500
          cropFocus: FACES
          resizingBehavior: FILL
        )
        title
      }
    }

    # Dirtily copied from Header.jsx
    ratgeber: contentfulNavigation(slug: { eq: "ratgeber" }) {
      entries {
        ... on ContentfulCategory {
          internal {
            type
          }
          id
          title
          slug
        }
      }
    }

    navigation: contentfulNavigation(slug: { eq: "hauptnavigation" }) {
      entries {
        ... on ContentfulCategory {
          internal {
            type
          }
          id
          title
          slug
        }
        ... on ContentfulNavigation {
          internal {
            type
          }
          id
          title
          slug
          entries {
            ... on ContentfulCategory {
              internal {
                type
              }
              id
              title
              slug
            }
            ... on ContentfulPage {
              internal {
                type
              }
              id
              slug
              title
              page {
                slug
                title
              }
            }
          }
        }
        ... on ContentfulPage {
          internal {
            type
          }
          id
          title
          slug
        }
      }
    }
  }
`;
